import { saveLastDirectory, loadLastDirectory } from './db';

// ═══════════════════════════════════════════════════════════════
// File System Access API (sekce 2.4c)
//
// Cílová platforma je Chrome — žádný fallback, žádná feature-detection větev
// (kromě jednoho úvodního checku, který jen slušně informuje uživatele
// v nepodporovaném prohlížeči, viz isSupported()).
// ═══════════════════════════════════════════════════════════════

export interface DirEntry {
  name: string;
  handle: FileSystemFileHandle;
}

export function isSupported(): boolean {
  return 'showDirectoryPicker' in window && 'showOpenFilePicker' in window;
}

/** Otevře výběr složky a zapamatuje ji. */
export async function pickDirectory(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
    await saveLastDirectory(handle);
    return handle;
  } catch {
    return null; // uživatel zrušil dialog
  }
}

/** Načte zapamatovanou složku; ověří/vyžádá oprávnění. */
export async function restoreDirectory(): Promise<FileSystemDirectoryHandle | null> {
  const handle = await loadLastDirectory();
  if (!handle) return null;
  const ok = await ensurePermission(handle, 'readwrite');
  return ok ? handle : null;
}

/** Vypíše .docx soubory ve složce (bez rekurze — zatím plochý výpis). */
export async function listDocx(dir: FileSystemDirectoryHandle): Promise<DirEntry[]> {
  const entries: DirEntry[] = [];
  // @ts-expect-error — async iterátor na directory handle (Chrome)
  for await (const [name, handle] of dir.entries()) {
    if (handle.kind === 'file' && name.toLowerCase().endsWith('.docx')) {
      entries.push({ name, handle: handle as FileSystemFileHandle });
    }
  }
  entries.sort((a, b) => a.name.localeCompare(b.name, 'cs'));
  return entries;
}

/** Otevře jeden .docx přes file picker (mimo composer stromu). */
export async function pickDocxFile(): Promise<FileSystemFileHandle | null> {
  try {
    const [handle] = await (window as any).showOpenFilePicker({
      types: [{ description: 'Word dokument', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] } }],
      multiple: false,
    });
    return handle ?? null;
  } catch {
    return null;
  }
}

/** Přečte obsah souboru jako ArrayBuffer. */
export async function readFile(handle: FileSystemFileHandle): Promise<ArrayBuffer> {
  const file = await handle.getFile();
  return await file.arrayBuffer();
}

/** Zapíše Blob zpět do existujícího souboru (skutečné Uložit). */
export async function writeFile(handle: FileSystemFileHandle, data: Blob): Promise<void> {
  const ok = await ensurePermission(handle, 'readwrite');
  if (!ok) throw new Error('Bez oprávnění k zápisu');
  const writable = await (handle as any).createWritable();
  await writable.write(data);
  await writable.close();
}

/** Uložit jako — nový soubor přes save picker. */
export async function saveFileAs(data: Blob, suggestedName: string): Promise<FileSystemFileHandle | null> {
  try {
    const handle: FileSystemFileHandle = await (window as any).showSaveFilePicker({
      suggestedName,
      types: [{ description: 'Word dokument', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] } }],
    });
    const writable = await (handle as any).createWritable();
    await writable.write(data);
    await writable.close();
    return handle;
  } catch {
    return null;
  }
}

/**
 * Ověří/vyžádá oprávnění k handle.
 * Prohlížeč se po restartu ptá na potvrzení — TO NEOBCHÁZÍME (viz bezpečnostní
 * požadavek 6): requestPermission musí být vyvolán z user gesta.
 */
export async function ensurePermission(
  handle: FileSystemHandle,
  mode: 'read' | 'readwrite',
): Promise<boolean> {
  const opts = { mode } as any;
  // @ts-expect-error — queryPermission je Chrome API
  if ((await handle.queryPermission(opts)) === 'granted') return true;
  // @ts-expect-error — requestPermission je Chrome API
  return (await handle.requestPermission(opts)) === 'granted';
}
